import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, LockKeyhole } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface GroupBotCardProps {
  member: {
    id: string;
    bot_id: string;
    priority: number;
    is_active: boolean;
    bots?: {
      name: string;
      status: string;
    };
  };
  isActiveInGroup: boolean;
  currentState?: {
    mode: 'OPEN' | 'GATE';
    updated_at: string;
    recent_results?: string;
    has_pending_non_active_enter?: boolean;
    is_permanent_locked?: boolean;
    lock_pattern?: string;
  };
  winRate?: number | null;
  totalExits?: number;
  onEditPriority: (memberId: string, currentPriority: number) => void;
  onRemove: (memberId: string) => void;
}

const formatTimestamp = (timestamp: string | null | undefined) => {
  if (!timestamp) return null;
  try {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  } catch {
    return null;
  }
};

export const GroupBotCard: React.FC<GroupBotCardProps> = ({
  member,
  isActiveInGroup,
  currentState,
  winRate,
  totalExits,
  onEditPriority,
  onRemove,
}) => {
  const botName = member.bots?.name || 'Unknown Bot';
  const isPermanentLocked = currentState?.is_permanent_locked === true;
  
  // Memoize expensive color calculations
  const { borderColor, bgColor } = useMemo(() => {
    // Border & background colors - PERMANENT LOCK gets highest priority
    const border = isPermanentLocked
      ? 'border-red-500 border-4 ring-2 ring-red-500/50 shadow-lg shadow-red-500/30'
      : isActiveInGroup
      ? 'border-blue-500 border-4 ring-2 ring-blue-500/50 shadow-lg shadow-blue-500/30'
      : member.priority === 1
      ? 'border-yellow-500 border-4 ring-2 ring-yellow-500/50 shadow-lg shadow-yellow-500/30'
      : currentState?.mode === 'OPEN'
      ? 'border-green-500 border-2'
      : 'border-slate-400 border-2';
    
    const bg = isPermanentLocked
      ? 'bg-red-500/20'
      : isActiveInGroup
      ? 'bg-blue-500/20'
      : member.priority === 1
      ? 'bg-yellow-500/15'
      : currentState?.mode === 'OPEN'
      ? 'bg-green-500/10'
      : 'bg-slate-400/10';
    
    return { borderColor: border, bgColor: bg };
  }, [isPermanentLocked, isActiveInGroup, member.priority, currentState?.mode]);

  return (
    <div
      className={cn(
        'rounded-lg p-2 transition-all duration-200 hover:shadow-lg flex flex-col h-full',
        borderColor,
        bgColor
      )}
    >
      {/* Permanent Lock Warning Banner */}
      {isPermanentLocked && (
        <div className="absolute top-0 left-0 right-0 bg-red-600 text-white text-[8px] py-1 px-2 flex items-center gap-1 rounded-t-lg">
          <LockKeyhole className="h-3 w-3 animate-pulse" />
          <span className="font-semibold">PERMANENT LOCK</span>
          <span className="font-mono ml-auto">{currentState.lock_pattern}</span>
        </div>
      )}

      {/* Header: Bot Name + Priority + Actions */}
      <div className={cn("flex items-start justify-between mb-1", isPermanentLocked && "mt-6")}>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-medium truncate flex items-center gap-1" title={botName}>
            {isActiveInGroup && <span className="text-blue-500 text-sm">👑</span>}
            {botName}
          </div>
        </div>
        
        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
          <Badge 
            variant={isActiveInGroup ? "default" : "secondary"} 
            className={cn(
              "text-[8px] h-4 px-1.5 font-bold",
              isActiveInGroup && "bg-blue-500 hover:bg-blue-600 animate-pulse"
            )}
          >
            {isActiveInGroup ? "🟦 ACTIVE" : `P${member.priority}`}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            className="h-4 w-4 p-0 hover:bg-transparent"
            onClick={() => onEditPriority(member.id, member.priority)}
          >
            <Edit className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-4 w-4 p-0 hover:bg-transparent text-destructive"
            onClick={() => {
              if (confirm(`Remove ${botName}?`)) onRemove(member.id);
            }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Mode Badge */}
      <div className="mb-1">
        <Badge 
          variant={currentState?.mode === 'OPEN' ? 'default' : 'secondary'} 
          className="text-[8px] h-4 px-1.5"
        >
          {currentState?.mode || 'N/A'}
        </Badge>
      </div>

      {/* Mode Display */}
      <div className="flex items-center justify-center mb-1">
        <span className="text-lg font-bold font-mono">
          {currentState?.mode || 'N/A'}
        </span>
      </div>

      {/* Pattern Display */}
      <div className="flex items-center justify-center flex-1">
        <span className="text-[10px] font-bold font-mono tracking-tight">
          {currentState?.recent_results || 'N/A'}
        </span>
      </div>

      {/* Timestamp */}
      <div className="text-[8px] text-muted-foreground text-center mb-1">
        {formatTimestamp(currentState?.updated_at) || 'No data'}
      </div>

      {/* Active Group Bot Indicator */}
      {isActiveInGroup && (
        <div className="border-t pt-2 mt-2">
          <div className="flex items-center justify-center">
            <Badge variant="default" className="text-[8px] h-5 px-2 bg-blue-500 hover:bg-blue-600">
              🟦 ACTIVE IN GROUP
            </Badge>
          </div>
        </div>
      )}

      {/* Pending Non-Active ENTER Indicator */}
      {currentState?.has_pending_non_active_enter && (
        <div className="border-t border-orange-500/30 pt-2 mt-2 bg-orange-500/5">
          <div className="flex flex-col items-center gap-1">
            <Badge variant="outline" className="text-[8px] h-5 px-2 border-orange-500 text-orange-500 animate-pulse">
              ⚠️ PENDING ENTER
            </Badge>
            <div className="text-[8px] text-orange-500 text-center">
              Next EXIT will be skipped
            </div>
          </div>
        </div>
      )}

      {/* Win Rate Display */}
      {winRate !== null && winRate !== undefined && totalExits !== undefined && totalExits > 0 && (
        <div className="border-t pt-2 mt-auto">
          <div className="flex flex-col items-center">
            <div className={cn(
              "text-4xl font-bold leading-none",
              winRate >= 60 ? "text-green-500" : 
              winRate >= 50 ? "text-orange-400" : 
              "text-red-500"
            )}>
              {Math.round(winRate)}
              <span className="text-lg ml-0.5">%</span>
            </div>
            <div className="text-[9px] text-muted-foreground mt-1">
              ({totalExits} exits)
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
